import type { ThemeSettings } from '@/theme/types';

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isEqual } from 'es-toolkit';

import { loadState } from './localStorage';

const SETTINGS_REDUCER_NAME = 'settings';

export const enum SETTINGS_REDUCER_STATUS {
  NOT_INITIATED = 'not_initiated',
  IDLE = 'idle',
  LOADING = 'loading',
  ERROR = 'error',
}

export type DisplaySettings = {
  navLayout?: 'vertical' | 'mini';
  navColor?: 'integrate' | 'apparent';
};

type SettingsState = {
  status: SETTINGS_REDUCER_STATUS;
  error: unknown;
  settings: {
    theme: ThemeSettings;
    display: DisplaySettings;
    isDrawerOpen: boolean;

    //Not used at the moment
    isCompactLayout: boolean;
  };
};

export const defaultThemeConfigs: ThemeSettings = {
  direction: 'ltr',
  fontSize: 16,
  fontFamily: {
    primary: 'Public Sans Variable',
    secondary: 'Barlow',
  },
  contrast: 'hight',
    primaryColor: {
      lighter: '#6689BA',
      light: '#3361A3',
      main: '#003A8C',
      dark: '#002275',
      darker: '#000A50',
      contrastText: '#FFFFFF',
    },
    secondaryColor: {
      lighter: '#A0A0A0',
      light: '#808080',
      main: '#616060',
      dark: '#414141',
      darker: '#222222',
      contrastText: '#FFFFFF',
    },
  colorScheme: 'light',
};

export const defaultDisplayConfigs: DisplaySettings = {
  navLayout: 'vertical',
  navColor: 'integrate',
};

const defaultState: SettingsState = {
  status: SETTINGS_REDUCER_STATUS.NOT_INITIATED,
  error: null,
  settings: {
    theme: defaultThemeConfigs,
    display: defaultDisplayConfigs,
    isDrawerOpen: false,
    isCompactLayout: true,
  },
};

const loadStorageUser = (): SettingsState => {
  const persistedState = loadState(SETTINGS_REDUCER_NAME);

  if (persistedState) {
    const time = new Date().getTime() / 1000;

    if (persistedState.expireOn > time) {
      return persistedState;
    }
  }

  return defaultState;
};

export const settingsSlice = createSlice({
  name: SETTINGS_REDUCER_NAME,
  initialState: loadStorageUser(),
  reducers: {
    onToggleDrawer: (state: SettingsState) => {
      state.settings.isDrawerOpen = !state.settings.isDrawerOpen;
    },
    onToggleNavBar: (state: SettingsState) => {
      state.settings.display.navLayout =
        state.settings.display.navLayout === 'vertical' ? 'mini' : 'vertical';
    },
    onCloseDrawer: (state: SettingsState) => {
      state.settings.isDrawerOpen = false;
    },
    onReset: (state: SettingsState) => {
      state.settings = defaultState.settings;
    },
    setThemeConfig: (state: SettingsState, action: PayloadAction<Partial<ThemeSettings>>) => {
      state.settings.theme = {
        ...state.settings.theme,
        ...action.payload,
      };
    },
    setDisplayConfig: (state: SettingsState, action: PayloadAction<Partial<DisplaySettings>>) => {
      state.settings.display = {
        ...state.settings.display,
        ...action.payload,
      };
    },
  },
  selectors: {
    themeConfigs: (state: SettingsState): ThemeSettings => state.settings.theme,
    displayConfigs: (state: SettingsState): DisplaySettings => state.settings.display,
    isDrawerOpen: (state: SettingsState): boolean => state.settings.isDrawerOpen,
    isCompactLayout: (state: SettingsState): boolean => state.settings.isCompactLayout,
    canReset: (state: SettingsState): boolean =>
      !(
        isEqual(state.settings.theme, defaultState.settings.theme) &&
        isEqual(state.settings.display, defaultState.settings.display)
      ),
  },
});

// ACTIONS
export const {
  onToggleDrawer,
  onToggleNavBar,
  onCloseDrawer,
  onReset,
  setThemeConfig,
  setDisplayConfig,
} = settingsSlice.actions;

// SELECTORS
export const { themeConfigs, displayConfigs, isDrawerOpen, isCompactLayout, canReset } =
  settingsSlice.selectors;

export default settingsSlice.reducer;
